import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ZoneMetadata } from "@common/ADAPT.Common.Model/methodology/zone";
import { RoleLocation, RoleLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-location";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { SystemEntity } from "@common/ADAPT.Common.Model/organisation/system-entity";
import { SystemLocation, SystemLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/system-location";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-supplementary-data";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { cacheLatest } from "@common/lib/utilities/rxjs-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { CommonIntegratedArchitectureFrameworkAuthService } from "@org-common/lib/architecture/common-integrated-architecture-framework-auth.service";
import { OrganisationMapService } from "@org-common/lib/architecture/organisation-map/organisation-map.service";
import { OrganisationMapSupplementaryData } from "@org-common/lib/architecture/organisation-map/organisation-map-supplementary-data.enum";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { DirectorySharedService } from "@org-common/lib/directory-shared/directory-shared.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { TeamsService } from "app/features/people/teams/teams.service";
import { SystemisationService } from "app/features/systemisation/systemisation.service";
import { OrganisationMapFilterService } from "app/organisation/organisation-map-filter/organisation-map-filter.service";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { distinctUntilChanged, filter, map, startWith, switchMap, take } from "rxjs/operators";
import { Options as SortablejsOptions } from "sortablejs";

@Component({
    selector: "adapt-organisation-explorer",
    templateUrl: "./organisation-explorer.component.html",
    styleUrls: ["./organisation-explorer.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class OrganisationExplorerComponent extends BaseComponent implements OnChanges {
    public readonly ZoneMetadata = ZoneMetadata;
    public readonly OrganisationMapSupplementaryData = OrganisationMapSupplementaryData;

    @Input() public filterTeams?: (teams: Team[]) => Team[] | Observable<Team[]>;
    @Input() public viewOption?: number;
    @Input() public supplementaryDataVisible: OrganisationMapSupplementaryData[] = [];
    @Input() public isEditing = false;
    @Output() public loaded = new EventEmitter<void>();

    public globalZoneTeams$: Observable<Team[]>;
    public showGlobalZone$: Observable<boolean>;
    public showEndedTeams$: Observable<boolean>;

    public sortableOptions: SortablejsOptions = {
        ...this.orgMapService.defaultSortableOptions,
        // no reordering for globals
        sort: false,
    };

    public draggingTeamCanBeDroppedIntoGlobalZone = true;

    // needed as we call these observables in the template.
    // without the cache, there is a new observable every detect cycle
    private teamSystemsCache = new Map<number, Observable<SystemEntity[]>>();
    private teamRolesCache = new Map<number, Observable<RoleLocation[]>>();

    public supplementaryDataPrime$: Observable<any>;

    public constructor(
        private commonDataService: CommonDataService,
        private commonTeamsService: CommonTeamsService,
        private teamsService: TeamsService,
        private orgMapService: OrganisationMapService,
        private rxjsBreezeService: RxjsBreezeService,
        private systemisationService: SystemisationService,
        private directorySharedService: DirectorySharedService,
        private authService: AuthorisationService,
        orgMapFilterService: OrganisationMapFilterService,
    ) {
        super();

        this.showEndedTeams$ = orgMapFilterService.filter$.pipe(
            map((filterParams) => filterParams.showEndedTeams),
            distinctUntilChanged(),
        );

        this.globalZoneTeams$ = combineLatest([
            this.orgMapService.hierarchyChanged$.pipe(startWith(undefined)),
            this.showEndedTeams$,
        ]).pipe(
            switchMap(() => this.teamsService.promiseToGetTeamsWithoutLocations(true)),
            switchMap((teams) => this.commonTeamsService.applyTeamsFilter(teams, this.filterTeams)),
            cacheLatest(),
        );

        this.showGlobalZone$ = this.globalZoneTeams$.pipe(
            map((teams) => teams.length > 0),
        );

        // TODO: this needs a performance pass.
        this.supplementaryDataPrime$ = orgMapFilterService.filter$.pipe(
            switchMap((f) => {
                const observables: Observable<any>[] = [];

                if (f.supplementaryData.includes(OrganisationMapSupplementaryData.Roles)) {
                    observables.push(this.commonDataService.getAll(RoleLocationBreezeModel));
                }

                if (f.supplementaryData.includes(OrganisationMapSupplementaryData.Accountabilities)) {
                    observables.push(this.commonDataService.getAll(TeamSupplementaryDataBreezeModel));
                }

                if (f.supplementaryData.includes(OrganisationMapSupplementaryData.Systems)) {
                    observables.push(this.authService.getHasAccess(CommonIntegratedArchitectureFrameworkAuthService.ReadSystem).pipe(
                        take(1),
                        filter((canReadSystem) => canReadSystem),
                        switchMap(() => this.commonDataService.getAll(SystemLocationBreezeModel)),
                    ));
                }

                return observables.length !== 0
                    ? forkJoin(observables)
                    : of([]);
            }),
            cacheLatest(),
        );

        this.orgMapService.mapEntityDragging$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((team) => {
            this.draggingTeamCanBeDroppedIntoGlobalZone = !team || !team.teamLocations || team.teamLocations.length <= 1;
        });
    }

    public teamHasChildren(teamId: number, includeInactiveTeams = false) {
        return this.orgMapService.teamHasChildren(teamId, includeInactiveTeams);
    }

    public teamMemberCount(team: Team) {
        return team.roleConnections
            .filter((rc) => rc?.role?.roleType?.code !== RoleTypeCode.TeamLeader && rc?.role?.roleType?.code !== RoleTypeCode.TeamInput)
            .length;
    }

    public getTeamSystems(team: Team): Observable<SystemEntity[]> {
        if (!this.teamSystemsCache.has(team.teamId)) {
            this.teamSystemsCache.set(team.teamId, this.supplementaryDataPrime$.pipe(
                switchMap(() => this.authService.promiseToGetHasAccess(CommonIntegratedArchitectureFrameworkAuthService.ReadSystem, team)),
                filter((hasAccess) => hasAccess),
                switchMap(() => this.rxjsBreezeService.entityTypeChanged(SystemLocation).pipe(
                    startWith(undefined))),
                switchMap(() => this.systemisationService.getSystemsForTeam(team))));
        }

        return this.teamSystemsCache.get(team.teamId)!;
    }

    public getTeamRoleLocations(team: Team) {
        if (!this.teamRolesCache.has(team.teamId)) {
            this.teamRolesCache.set(team.teamId, this.supplementaryDataPrime$.pipe(
                switchMap(() => this.rxjsBreezeService.entityTypeChanged(RoleLocation).pipe(
                    startWith(undefined))),
                switchMap(() => this.directorySharedService.getTeamRoleLocations(team).pipe(
                    map(({ teamRoleLocations }) => teamRoleLocations)))));
        }

        return this.teamRolesCache.get(team.teamId)!;
    }

    public ngOnChanges() {
        this.sortableOptions = { ...this.sortableOptions, disabled: !this.isEditing };
    }
}
